import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import LocationScheduleEditor from './LocationScheduleEditor';
import { setMobileViewPreference } from '../utils/mobile';

const MobileNavigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const { currentUser, logout } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const menuButton = useRef(null);
  useEffect(() => {
    if (!currentUser) navigate('/login', { replace: true });
  }, [currentUser, navigate]);
  const canManageOwnSchedule = currentUser?.user?.orgRole === 'client'
    || currentUser?.user?.role === 'instructor'
    || Number(currentUser?.user?.roleId) === 3;
  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!isMenuOpen) return;
    const previousOverflow = document.body.style.overflowY;
    document.body.style.overflowY = 'hidden';
    const closeOnEscape = event => {
      if (event.key === 'Escape') { setIsMenuOpen(false); menuButton.current?.focus(); }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => { document.body.style.overflowY = previousOverflow; window.removeEventListener('keydown', closeOnEscape); };
  }, [isMenuOpen]);

  const menuItems = [
    { name: 'Flight Schedule', path: '/calendar', icon: '📅' },
    { name: 'Events', path: '/events', icon: '📆' },
    { name: 'Aircraft', path: '/aircraft', icon: '✈️', roles: ['admin', 'superuser', 'staff', 'instructor'] },
    { name: 'People', path: '/people', icon: '👥', roles: ['admin', 'superuser', 'staff', 'instructor'] },
    { name: 'Training', path: '/training', icon: '📚' },
    { name: 'Manage', path: '/manage', icon: '⚙️', roles: ['admin', 'superuser', 'instructor'] },
  ];

  const filteredMenuItems = menuItems.filter(item => {
    if (!item.roles) return true; // No role restriction
    return item.roles.includes(currentUser?.user?.orgRole || currentUser?.orgRole);
  });

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Top Navigation Bar */}
      <div className="mobile-navigation-bar" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '72px',
        backgroundColor: '#007bff',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 1rem',
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
          <img src={`${process.env.PUBLIC_URL}/Real_AIr_Scheduling_logo.png`} alt="Real Air Scheduling" style={{ width: '48px', height: '48px', flex: '0 0 48px', objectFit: 'contain' }} />
          <button
            ref={menuButton}
            type="button"
            aria-label="Open navigation"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation-menu"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            ☰
          </button>
        </div>
        
        <h1 style={{ flex: '1 1 auto', minWidth: 0, margin: '0 0.5rem', overflowWrap: 'anywhere', textAlign: 'center', fontSize: '17px', lineHeight: 1.15, fontWeight: 'bold' }}>
          Real Air Scheduling
        </h1>
        
        <div style={{ fontSize: '13px', maxWidth: '64px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {currentUser?.user?.firstName || currentUser?.firstName || 'User'}
        </div>
      </div>

      {/* Slide-out Menu */}
      {isMenuOpen && <nav id="mobile-navigation-menu" aria-label="Main navigation" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: 'min(300px, calc(100vw - 24px))',
        height: '100dvh',
        overflowY: 'auto',
        backgroundColor: 'white',
        boxShadow: '2px 0 10px rgba(0,0,0,0.1)',
        transition: 'left 0.3s ease',
        zIndex: 1201,
        paddingTop: 'calc(56px + env(safe-area-inset-top, 0px))',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}>
        <button type="button" aria-label="Close navigation" title="Close" onClick={() => { setIsMenuOpen(false); menuButton.current?.focus(); }} style={{ position: 'absolute', top: 'calc(8px + env(safe-area-inset-top, 0px))', right: '12px', background: 'transparent', border: 0, fontSize: '24px', cursor: 'pointer' }}>×</button>
        <div style={{ padding: '1rem' }}>
          <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
              {currentUser?.user?.firstName || currentUser?.firstName} {currentUser?.user?.lastName || currentUser?.lastName}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {currentUser?.user?.orgRole || currentUser?.orgRole}
            </div>
          </div>
          
          {filteredMenuItems.map((item) => (
            <button type="button"
              key={item.name}
              aria-current={location.pathname === item.path ? 'page' : undefined}
              onClick={() => handleNavigation(item.path)}
              style={{
                display: 'flex',
                width: '100%',
                textAlign: 'left',
                alignItems: 'center',
                padding: '1rem',
                marginBottom: '0.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: location.pathname === item.path ? '#f0f8ff' : 'transparent',
                border: location.pathname === item.path ? '1px solid #007bff' : '1px solid transparent'
              }}
            >
              <span style={{ fontSize: '20px', marginRight: '1rem' }}>{item.icon}</span>
              <span style={{ fontSize: '16px', color: '#333' }}>{item.name}</span>
            </button>
          ))}
          {canManageOwnSchedule && <button type="button" onClick={() => { setIsMenuOpen(false); setShowSchedule(true); }} style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '1rem', marginBottom: '0.5rem', backgroundColor: 'transparent', border: '1px solid transparent', borderRadius: '8px', textAlign: 'left', cursor: 'pointer' }}>
            <span aria-hidden="true" style={{ fontSize: '20px', marginRight: '1rem' }}>📅</span><span style={{ fontSize: '16px', color: '#333' }}>My Schedule</span>
          </button>}
          <button type="button" onClick={() => { setIsMenuOpen(false); setMobileViewPreference('desktop'); }} style={{ display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', padding: '1rem', marginTop: '0.5rem', borderRadius: '8px', cursor: 'pointer', backgroundColor: '#f0f4f6', border: '1px solid #d6e0e7' }}>
            <span aria-hidden="true" style={{ fontSize: '20px', marginRight: '1rem' }}>🖥️</span><span style={{ fontSize: '16px', color: '#333' }}>View Desktop Site</span>
          </button>
          
          <button type="button"
            onClick={handleLogout}
            style={{
              display: 'flex',
              width: '100%',
              textAlign: 'left',
              alignItems: 'center',
              padding: '1rem',
              marginTop: '2rem',
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6'
            }}
          >
            <span style={{ fontSize: '20px', marginRight: '1rem' }}>🚪</span>
            <span style={{ fontSize: '16px', color: '#333' }}>Logout</span>
          </button>
        </div>
      </nav>}

      {/* Overlay */}
      {isMenuOpen && (
        <div
          onClick={() => setIsMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1200
          }}
        />
      )}
      {showSchedule && <div onMouseDown={event => { if (event.target === event.currentTarget) setShowSchedule(false); }} style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }}>
        <div className="my-schedule-dialog" role="dialog" aria-modal="true" aria-label="Edit My Schedule" style={{ position: 'relative', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', padding: '1.5rem', width: 'min(850px, 95%)', height: 'min(850px, 90dvh)', overflow: 'hidden', borderRadius: '8px' }}>
          <button type="button" title="Close" aria-label="Close Edit My Schedule" onClick={() => setShowSchedule(false)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 0, fontSize: '1.5rem', cursor: 'pointer', zIndex: 1 }}>×</button>
          <h3 className="my-schedule-title" style={{ paddingRight: '3.5rem', flexShrink: 0 }}>Edit My Schedule</h3>
          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, flex: 1 }}>
            <LocationScheduleEditor fixedFooter resourceId={currentUser?.user?.id ?? currentUser?.id} baseRate={currentUser?.user?.rate} />
          </div>
        </div>
      </div>}
    </>
  );
};

export default MobileNavigation;